import { useEffect, useRef, useState } from 'react';
import { useColourTheme } from '@/ui/theme/ColourThemeContext';
import {
  CheckIconV2,
  CopyIcon,
  EyeToSeeIcon,
  Tooltip,
  TOOLTIP_SURFACE_BOTTOM,
  YellowWarningIcon,
} from '@repo/atomic-components';
import { copyToClipboard } from '@repo/utils';

/** Panel chrome for the citation hover card; shared by every surface that shows one. */
export const CITATION_TOOLTIP_SURFACE_CLASS =
  '!bg-theme-white !border-theme-border-grey !text-theme-black !p-0 !rounded-lg !shadow-lg overflow-hidden';

const COPY_CHECK_DURATION_MS = 3000;

/** Shown above the extract when it's an AI description of an image, not text from the document. */
export const AI_IMAGE_CITATION_WARNING =
  'This information has come from an AI-generated description of an image, please check the source carefully for accuracy';

interface CitationTooltipPanelProps {
  /** Citation number, shown in the pill on the left of the header. */
  number: number;
  /** Full citation extract shown in the tooltip body. */
  citationText: string;
  /** Source document title shown in the tooltip header. */
  documentName?: string;
  /** Optional 1-based page reference shown next to the source title. */
  pageNumber?: number;
  /** Opens the cited source document. */
  onOpenSource: () => void;
  /** Extract is a model-written image description, so the card leads with a warning. */
  isAiImageDescription?: boolean;
}

/**
 * Citation hover card body: source header with copy/open, and the extract. Shared by the response
 * text, Sources panel and draft.
 */
export const CitationTooltipPanel = ({
  number,
  citationText,
  documentName,
  pageNumber,
  onOpenSource,
  isAiImageDescription,
}: CitationTooltipPanelProps) => {
  const { theme } = useColourTheme();
  const pillBgClass = theme === 'dark' ? 'bg-theme-white' : 'bg-fletchers-gray-110';

  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const openBtnRef = useRef<HTMLButtonElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!citationText.trim()) return;
    await copyToClipboard(citationText);
    setIsCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setIsCopied(false), COPY_CHECK_DURATION_MS);
  };

  return (
    <div className="text-theme-black flex w-full flex-col text-sm">
      {isAiImageDescription && (
        <div className="bg-theme-background-yellow border-theme-border-grey flex items-start gap-2 border-b px-4 py-3">
          <YellowWarningIcon width={16} height={16} className="mt-[2px] shrink-0" aria-hidden />
          <p className="text-theme-black m-0 leading-5">
            <span className="font-semibold">Warning:</span> {AI_IMAGE_CITATION_WARNING}
          </p>
        </div>
      )}
      <div className="border-theme-border-grey flex items-center gap-2 border-b px-4 py-3">
        <span
          className={`${pillBgClass} text-theme-black inline-flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded text-xs font-semibold`}
        >
          {number}
        </span>
        <span className="text-theme-black min-w-0 truncate font-semibold">{documentName ?? 'Source'}</span>
        {typeof pageNumber === 'number' && (
          <span className="text-theme-text-grey shrink-0 text-sm">Page {pageNumber}</span>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            ref={copyBtnRef}
            type="button"
            onClick={handleCopy}
            aria-label={isCopied ? 'Copied to clipboard' : 'Copy to clipboard'}
            className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
          >
            {isCopied ? <CheckIconV2 width={14} height={12} /> : <CopyIcon className="overflow-visible" />}
          </button>
          <Tooltip
            target={copyBtnRef as React.RefObject<HTMLElement>}
            content={isCopied ? 'Copied to clipboard' : 'Copy to clipboard'}
            position="bottom"
            className={TOOLTIP_SURFACE_BOTTOM}
            baseZIndex={10001}
          />
          <button
            ref={openBtnRef}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenSource();
            }}
            aria-label="Open source document"
            className="text-theme-text-grey hover:text-theme-black flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent p-0 transition-colors"
          >
            <EyeToSeeIcon />
          </button>
          <Tooltip
            target={openBtnRef as React.RefObject<HTMLElement>}
            content="Open source"
            position="bottom"
            className={TOOLTIP_SURFACE_BOTTOM}
            baseZIndex={10001}
          />
        </div>
      </div>
      <div className="max-h-[220px] overflow-y-auto px-4 py-3 leading-5 [overflow-wrap:anywhere]">
        &ldquo;{citationText}&rdquo;
      </div>
    </div>
  );
};
